import React, { createContext, useContext, useState, useCallback } from 'react';
import './NotificationSystem.css';

const NotificationContext = createContext();

export const useNotification = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
};

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([]);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [promptDialog, setPromptDialog] = useState(null);
  const [choiceDialog, setChoiceDialog] = useState(null);

  const showNotification = useCallback((message, type = 'info') => {
    const id = Date.now();
    const notification = { id, message, type };
    
    setNotifications(prev => [...prev, notification]);
    
    // Auto-remove after 4 seconds
    setTimeout(() => {
      setNotifications(prev => prev.filter(n => n.id !== id));
    }, 4000);
  }, []);

  const showSuccess = useCallback((message) => {
    showNotification(message, 'success');
  }, [showNotification]);

  const showError = useCallback((message) => {
    showNotification(message, 'error');
  }, [showNotification]);

  const showInfo = useCallback((message) => {
    showNotification(message, 'info');
  }, [showNotification]);

  const confirm = useCallback((message, title = 'Confirm') => {
    return new Promise((resolve) => {
      setConfirmDialog({
        title,
        message,
        onConfirm: () => {
          setConfirmDialog(null);
          resolve(true);
        },
        onCancel: () => {
          setConfirmDialog(null);
          resolve(false);
        }
      });
    });
  }, []);

  const prompt = useCallback((message, title = 'Input Required', defaultValue = '') => {
    return new Promise((resolve) => {
      setPromptDialog({
        title,
        message,
        defaultValue,
        onConfirm: (value) => {
          setPromptDialog(null);
          resolve(value);
        },
        onCancel: () => {
          setPromptDialog(null);
          resolve(null);
        }
      });
    });
  }, []);

  const choice = useCallback((message, title = 'Choose Option', options = []) => {
    return new Promise((resolve) => {
      setChoiceDialog({
        title,
        message,
        options,
        selectedOption: options[0]?.value || null,
        onConfirm: (value) => {
          setChoiceDialog(null);
          resolve(value);
        },
        onCancel: () => {
          setChoiceDialog(null);
          resolve(null);
        }
      });
    });
  }, []);

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  }, []);

  return (
    <NotificationContext.Provider value={{ 
      showNotification, 
      showSuccess, 
      showError, 
      showInfo,
      confirm,
      prompt,
      choice
    }}>
      {children}
      
      {/* Toast Notifications */}
      <div className="notification-container">
        {notifications.map(notification => (
          <div 
            key={notification.id} 
            className={`notification notification-${notification.type}`}
            onClick={() => removeNotification(notification.id)}
          >
            <div className="notification-icon">
              {notification.type === 'success' && '✓'}
              {notification.type === 'error' && '✕'}
              {notification.type === 'info' && 'ℹ'}
            </div>
            <div className="notification-message">{notification.message}</div>
            <button 
              className="notification-close"
              onClick={(e) => {
                e.stopPropagation();
                removeNotification(notification.id);
              }}
            >
              ✕
            </button>
          </div>
        ))}
      </div>

      {/* Confirmation Dialog */}
      {confirmDialog && (
        <div className="confirm-dialog-backdrop" onClick={confirmDialog.onCancel}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-dialog-header">
              <h3>{confirmDialog.title}</h3>
            </div>
            <div className="confirm-dialog-body">
              <p>{confirmDialog.message}</p>
            </div>
            <div className="confirm-dialog-footer">
              <button 
                className="confirm-btn confirm-btn-cancel" 
                onClick={confirmDialog.onCancel}
              >
                Cancel
              </button>
              <button 
                className="confirm-btn confirm-btn-confirm" 
                onClick={confirmDialog.onConfirm}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Prompt Dialog */}
      {promptDialog && (
        <div className="confirm-dialog-backdrop" onClick={promptDialog.onCancel}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-dialog-header">
              <h3>{promptDialog.title}</h3>
            </div>
            <div className="confirm-dialog-body">
              <p>{promptDialog.message}</p>
              <input
                type="text"
                className="prompt-input"
                defaultValue={promptDialog.defaultValue}
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    promptDialog.onConfirm(e.target.value);
                  } else if (e.key === 'Escape') {
                    promptDialog.onCancel();
                  }
                }}
              />
            </div>
            <div className="confirm-dialog-footer">
              <button 
                className="confirm-btn confirm-btn-cancel" 
                onClick={promptDialog.onCancel}
              >
                Cancel
              </button>
              <button 
                className="confirm-btn confirm-btn-confirm" 
                onClick={(e) => {
                  const input = e.target.closest('.confirm-dialog').querySelector('.prompt-input');
                  promptDialog.onConfirm(input.value);
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Choice Dialog */}
      {choiceDialog && (
        <div className="confirm-dialog-backdrop" onClick={choiceDialog.onCancel}>
          <div className="confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="confirm-dialog-header">
              <h3>{choiceDialog.title}</h3>
            </div>
            <div className="confirm-dialog-body">
              <p>{choiceDialog.message}</p>
              <div className="choice-options">
                {choiceDialog.options.map((option, index) => (
                  <label key={index} className="choice-option">
                    <input
                      type="radio"
                      name="choice"
                      value={option.value}
                      defaultChecked={index === 0}
                      onChange={(e) => {
                        choiceDialog.selectedOption = e.target.value;
                      }}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </div>
            <div className="confirm-dialog-footer">
              <button 
                className="confirm-btn confirm-btn-cancel" 
                onClick={choiceDialog.onCancel}
              >
                Cancel
              </button>
              <button 
                className="confirm-btn confirm-btn-confirm" 
                onClick={() => {
                  const selected = document.querySelector('input[name="choice"]:checked')?.value;
                  choiceDialog.onConfirm(selected || choiceDialog.options[0]?.value);
                }}
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}
    </NotificationContext.Provider>
  );
}
